import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress, useTheme } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const PULL_THRESHOLD = 70;
const MAX_TRANSLATE = 60;
const INDICATOR_SIZE = 40;
const INDICATOR_HOLD = INDICATOR_SIZE + 6;

type Props = {
    onRefresh: () => Promise<void> | void;
    children: React.ReactNode;
    style?: React.CSSProperties;
    contentStyle?: React.CSSProperties;
};

export function PullToRefresh({ onRefresh, children, style, contentStyle }: Props) {
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';
    const primary = theme.palette.primary.main;

    const innerRef = useRef<HTMLDivElement>(null);
    const onRefreshRef = useRef(onRefresh);
    onRefreshRef.current = onRefresh;

    const startYRef = useRef<number | null>(null);
    const pullDistRef = useRef(0);
    const activeRef = useRef(false);

    const [phase, setPhase] = useState<'idle' | 'pulling' | 'releasing' | 'refreshing'>('idle');
    const [pullPct, setPullPct] = useState(0);

    const resetInner = (animate: boolean) => {
        const el = innerRef.current;
        if (!el) return;
        if (animate) {
            el.style.transition = 'transform 0.25s ease';
            el.style.transform = 'translateY(0px)';
            setTimeout(() => { if (innerRef.current) innerRef.current.style.transition = ''; }, 260);
        } else {
            el.style.transition = '';
            el.style.transform = 'translateY(0px)';
        }
    };

    useEffect(() => {
        const el = innerRef.current;
        if (!el) return;

        const onTouchStart = (e: TouchEvent) => {
            startYRef.current = e.touches[0].clientY;
            pullDistRef.current = 0;
            activeRef.current = false;
        };

        const onTouchMove = (e: TouchEvent) => {
            if (startYRef.current === null) return;
            const dy = e.touches[0].clientY - startYRef.current;
            if (dy <= 0 || el.scrollTop > 0) return;
            e.preventDefault();
            activeRef.current = true;
            pullDistRef.current = dy;
            const translate = Math.min(dy * 0.6, MAX_TRANSLATE);
            el.style.transform = `translateY(${translate}px)`;
            const pct = Math.min(dy / PULL_THRESHOLD, 1);
            setPullPct(pct);
            setPhase(dy >= PULL_THRESHOLD ? 'releasing' : 'pulling');
        };

        const onTouchEnd = async () => {
            if (startYRef.current === null) return;
            startYRef.current = null;
            const dist = pullDistRef.current;
            pullDistRef.current = 0;

            if (!activeRef.current) return;
            activeRef.current = false;

            if (dist >= PULL_THRESHOLD) {
                if (innerRef.current) {
                    innerRef.current.style.transition = 'transform 0.2s ease';
                    innerRef.current.style.transform = `translateY(${INDICATOR_HOLD}px)`;
                    setTimeout(() => { if (innerRef.current) innerRef.current.style.transition = ''; }, 220);
                }
                setPhase('refreshing');
                try {
                    await Promise.resolve(onRefreshRef.current());
                } finally {
                    resetInner(true);
                    setPhase('idle');
                    setPullPct(0);
                }
            } else {
                resetInner(true);
                setPhase('idle');
                setPullPct(0);
            }
        };

        const onTouchCancel = () => {
            startYRef.current = null;
            activeRef.current = false;
            resetInner(true);
            setPhase('idle');
            setPullPct(0);
        };

        el.addEventListener('touchstart', onTouchStart, { passive: true });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd, { passive: true });
        el.addEventListener('touchcancel', onTouchCancel, { passive: true });

        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
            el.removeEventListener('touchcancel', onTouchCancel);
        };
    }, []);

    const indicatorVisible = phase !== 'idle';

    return (
        <div style={{ position: 'relative', overflow: 'hidden', ...style }}>
            {/* Indicator sits above the content, revealed when inner slides down */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: INDICATOR_SIZE,
                height: INDICATOR_SIZE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                pointerEvents: 'none',
            }}>
                {phase === 'refreshing' ? (
                    <CircularProgress size={24} thickness={3} sx={{ color: primary }} />
                ) : (
                    <div style={{
                        width: INDICATOR_SIZE,
                        height: INDICATOR_SIZE,
                        borderRadius: '50%',
                        background: isLight ? '#fff' : theme.palette.background.paper,
                        boxShadow: isLight
                            ? '0 2px 8px rgba(180,83,9,0.18)'
                            : '0 2px 8px rgba(0,0,0,0.35)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: indicatorVisible ? pullPct : 0,
                        transition: 'opacity 0.1s',
                    }}>
                        <KeyboardArrowDownIcon sx={{
                            color: primary,
                            fontSize: 22,
                            transform: phase === 'releasing' ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                        }} />
                    </div>
                )}
            </div>

            {/* Scrollable content */}
            <div
                ref={innerRef}
                style={{
                    height: '100%',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    willChange: 'transform',
                    ...contentStyle,
                }}
            >
                {children}
            </div>
        </div>
    );
}
